import { z } from 'zod'
import { Plus } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import PostCardSkeleton from 'components/Skeletons/PostCardSkeleton'
import { FC, useEffect } from 'react'
import { PollCreate, Post } from '@wellesley/openapi'
import { Form, TooltipWrapper, Button, cn } from '@wellesley/ui'

import Options from './Options'
import PollTypeSelect from './PollTypeSelect'
import PollTimeSelect from './PollTimeSelect'
import { convertPollToPollCreate } from './helpers'

type CreatePollSectionProps = {
  text: string
  feed?: Post
  isLoading: boolean
  textInputRef: React.RefObject<HTMLInputElement>
  onTextUpdate: (v: string) => void
  onPollChange: (data: PollCreate) => void
}

const pollSchema = z.object({
  options: z
    .array(z.string())
    .min(2, 'At least 2 options are required')
    .refine(
      (items) => new Set(items).size === items.length,
      'Options must be unique',
    ),
})

const MAX_OPTIONS = 4

const CreatePollSection: FC<CreatePollSectionProps> = ({
  feed,
  text,
  isLoading,
  textInputRef,
  onTextUpdate,
  onPollChange,
}) => {
  const form = useForm<PollCreate>({
    resolver: zodResolver(pollSchema),
    defaultValues: convertPollToPollCreate(feed),
  })

  const values = form.getValues()
  const options = form.watch('options')
  const optionsKey = options.join(',')

  useEffect(() => {
    onPollChange(values)
  }, [optionsKey, values.expiresIn, values.multiple, values.hideTotals])

  const handleAddOption = () => {
    form.setValue(`options.${options.length}`, '', {
      shouldDirty: true,
    })
  }

  const handleRemoveOption = (index: number) => {
    const newOptions = [...options]
    newOptions.splice(index, 1)
    form.setValue('options', newOptions, { shouldValidate: true })
  }

  const isOptionsLimitReached = options.length >= MAX_OPTIONS

  useEffect(() => {
    const timeId = setTimeout(() => {
      textInputRef.current?.focus()
    }, 0)

    return () => clearTimeout(timeId)
  }, [])

  if (isLoading)
    return (
      <PostCardSkeleton
        className="w-full"
        showMedia={false}
        showAvatar={false}
        showFooter={false}
        showPollsOptions
      />
    )

  return (
    <div className="gap-l flex flex-col">
      <input
        autoFocus
        ref={textInputRef}
        value={text}
        placeholder="Type your poll question here..."
        onChange={(e) => onTextUpdate(e.target.value)}
        className="text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus-visible:outline-none"
      />
      <Form {...form}>
        <div className="gap-xxs flex flex-col">
          <Options
            form={form}
            options={options}
            handleRemoveOption={handleRemoveOption}
          />
          <TooltipWrapper
            size="small"
            theme="dark"
            align="start"
            triggerProps={{
              asChild: true,
            }}
            tooltipText={
              isOptionsLimitReached ? 'Maximum number of options reached' : ''
            }
          >
            <Button
              variant="none"
              size="none"
              onClick={isOptionsLimitReached ? undefined : handleAddOption}
              className={cn(
                'mr-xl -mt-xxs p-xxs rounded-m gap-xxs gap-xxs flex min-h-12 flex-1 items-center justify-start border border-dashed border-gray-300 text-base font-normal text-gray-400',
                { 'border-gray-200 text-gray-300': isOptionsLimitReached },
              )}
            >
              <Plus
                size={24}
                color={
                  isOptionsLimitReached ? 'var(--gray-300)' : 'var(--gray-400)'
                }
              />
              Add option
            </Button>
          </TooltipWrapper>
          <div className="gap-s mt-s mb-l flex">
            <PollTypeSelect
              value={form.watch('multiple')}
              onChange={(value) =>
                form.setValue('multiple', value, { shouldDirty: true })
              }
            />
            <PollTimeSelect
              value={form.watch('expiresIn')}
              onChange={(value) => {
                form.setValue('expiresIn', +value, {
                  shouldDirty: true,
                })
              }}
            />
          </div>
        </div>
      </Form>
    </div>
  )
}

export default CreatePollSection
